import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import useSessionStore from "../stores/sessionStore.js";
import { useAcceptInvite } from "../hooks/useTeamQuery.js";
import Login from "../components/Login.jsx";
import Spinner from "../components/Spinner.jsx";

export default function InviteAccept() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSessionStore((s) => s.user);
  const setUser = useSessionStore((s) => s.setUser);
  const token = new URLSearchParams(location.search).get("token");

  const { mutate, isPending, isError, error, isSuccess } = useAcceptInvite();
  const hasFired = useRef(false); // guard against React StrictMode double-mount

  useEffect(() => {
    if (!user || !token || hasFired.current) return;
    hasFired.current = true;
    mutate(token, {
      onSuccess: (data) => {
        // data includes _csrf from the response header (injected by useAcceptInvite mutationFn)
        localStorage.setItem("fungistics-profile", JSON.stringify(data));
        setUser({ ...user, profile: data });
        queryClient.invalidateQueries(); // invalidate everything so stale team data refreshes
        navigate("/", { replace: true });
      },
    });
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Not logged in — show Login inline
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-6">
          Sign in to accept your team invitation.
        </p>
        <Login onSuccess={(identity) => setUser(identity)} />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-center text-red-600 dark:text-red-400">Invalid invite link.</p>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex flex-col items-center gap-4 mt-20">
        <Spinner />
        <p className="text-gray-500 dark:text-gray-400">Accepting your invitation…</p>
      </div>
    );
  }

  if (isError) {
    const status = error?.status;
    const code = error?.body?.error;
    let msg = "Something went wrong. Please try again or contact support.";
    if (status === 403 || code === "email_mismatch") {
      msg = "This invite was sent to a different email address. Make sure you're signed in as the correct account.";
    } else if (code === "invalid_token" || code === "expired") {
      msg = "This invite link has expired or is no longer valid.";
    } else if (status === 402) {
      msg = "This team has reached its member limit.";
    } else if (code === "already_member") {
      msg = "You're already a member of this team.";
    }
    return (
      <div className="flex flex-col items-center gap-4 mt-20">
        <p className="text-red-600 dark:text-red-400 text-center max-w-sm">{msg}</p>
        <button
          onClick={() => navigate("/")}
          className="text-sky-700 dark:text-sky-400 underline text-sm"
        >
          Go to dashboard
        </button>
      </div>
    );
  }

  // isSuccess handled by navigate in onSuccess; renders briefly before redirect
  return null;
}
